/**
 * AboutMe.jsx
 *
 * Renders src/content/about.md as the page body and src/content/profile.json
 * as the header. To update content, edit those files — no JSX needed.
 *
 * Module Tags guide:
 *   [MODULE-GAMES]   — places the games grid from module-games.json at that exact spot
 *   [MODULE-ARTISTS] — places the artists grid from module-artists.json at that exact spot
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import aboutContent from './content/about.md?raw';
import profile from './content/profile.json';
import gamesData from './content/module-games.json';
import artistsData from './content/module-artists.json';
import GameCard from './GameCard';
import ArtistCard from './ArtistCard';

function GamesList() {
  if (!gamesData || gamesData.length === 0) return null;
  return (
    <div className="games-grid">
      {gamesData.map((game) => (
        <GameCard key={game.id || game.title} game={game} />
      ))}
    </div>
  );
}

function ArtistsList() {
  if (!artistsData || artistsData.length === 0) return null;
  return (
    <div className="artists-grid">
      {artistsData.map((artist) => (
        <ArtistCard key={artist.id || artist.name} artist={artist} />
      ))}
    </div>
  );
}

// ── Custom renderers — maps markdown elements to the design system ────────

const components = {
  // ## Heading → divider + small-caps section label
  h2: ({ children }) => (
    <>
      <hr className="about-divider" />
      <h2 className="about-section-title">{children}</h2>
    </>
  ),

  // Paragraphs — check for embed tags like [MODULE-GAMES] and [MODULE-ARTISTS]
  p: ({ children }) => {
    const text = Array.isArray(children)
      ? children.join('')
      : String(children ?? '');

    const trimmed = text.trim().toLowerCase();

    if (
      trimmed === '[module-games]' ||
      trimmed === '[games]' ||
      trimmed === '<games/>' ||
      trimmed === '<games />'
    ) {
      return <GamesList />;
    }

    if (
      trimmed === '[module-artists]' ||
      trimmed === '[artists]' ||
      trimmed === '<artists/>' ||
      trimmed === '<artists />'
    ) {
      return <ArtistsList />;
    }

    return <p className="about-body-text">{children}</p>;
  },

  // Unordered list — renders as pill link chips
  ul: ({ children }) => (
    <div className="about-links">{children}</div>
  ),

  // List item — unwrap bullet point
  li: ({ children }) => (
    <span>{children}</span>
  ),

  // Links → pill chips
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="about-link"
    >
      {children}
    </a>
  ),
};

export default function AboutMe() {
  return (
    <div className="about-container">

      {/* Profile header — edit src/content/profile.json */}
      <div className="about-header">
        <div className="about-avatar">{profile.avatarLetter}</div>
        <div>
          <p className="about-name gradient-text">{profile.name}</p>
          <p className="about-tagline">{profile.tagline}</p>
        </div>
      </div>

      {/* Markdown content — edit src/content/about.md */}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={components}
      >
        {aboutContent}
      </ReactMarkdown>

    </div>
  );
}
